import { FC, Fragment } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import styled from 'styled-components';

import { Label } from '@/components/Forms';
import Text from '@/components/Text';
import { paymentMethodLabels, paymentMethods } from '@/utils/data/payments';
import { PaymentMethod } from '@/utils/enums';

const Box = styled.div`
  /* Equal-width options that drop to fewer columns rather than squeezing the
     labels when the modal column is narrow. */
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px 16px;
`;

const Selection = styled.label`
  display: flex;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  height: 46px;
  padding: 0 12px;
  align-items: center;
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  gap: 9px;
  white-space: nowrap;
  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.purple};
  }
`;

const Radio = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${({ theme }) => theme.colors.gray};
  border-radius: 50%;
  position: relative;
  transition: border-color ease-out 0.2s;
`;

const RadioCircle = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  position: absolute;
  margin: auto;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  transition: background ease-out 0.2s;
`;

const Input = styled.input`
  &:checked {
    + ${Selection} {
      border-color: ${({ theme }) => theme.colors.purple};
      ${Radio} {
        border-color: ${({ theme }) => theme.colors.purple};
      }
      ${RadioCircle} {
        background: ${({ theme }) => theme.colors.purple};
      }
    }
  }
  &:disabled {
    + ${Selection} {
      border-color: ${({ theme }) => theme.colors.grayscale};
      cursor: not-allowed;
      ${Radio} {
        border-color: ${({ theme }) => theme.colors.gray};
      }
      ${RadioCircle} {
        background: transparent;
      }
      ${Text} {
        color: ${({ theme }) => theme.colors.gray};
      }
    }
  }
  &:checked&:disabled {
    + ${Selection} {
      ${RadioCircle} {
        background: ${({ theme }) => theme.colors.gray};
      }
    }
  }
`;

interface Props {
  paymentMethod: PaymentMethod;
  setValue: UseFormSetValue<any>;
  shouldDisableType?: boolean;
  disabledMethods?: PaymentMethod[];
  // Which methods to offer at all — narrower than `paymentMethods` where a
  // method is no longer accepted for new invoices.
  methods?: PaymentMethod[];
}

const InvoicesReceiverType: FC<Props> = ({
  paymentMethod,
  setValue,
  shouldDisableType = false,
  disabledMethods = [],
  methods = paymentMethods,
}) => {
  return (
    <>
      <Label>Payment Options</Label>
      <Box>
        {methods.map((method) => (
          <Fragment key={method}>
            <Input
              checked={paymentMethod === method}
              name="transfer_type"
              type="radio"
              id={method}
              hidden
              onChange={() => setValue('payment_method', method)}
              disabled={shouldDisableType || disabledMethods.includes(method)}
            />
            <Selection htmlFor={method}>
              <Radio>
                <RadioCircle />
              </Radio>
              <Text $type="caption">{paymentMethodLabels[method]}</Text>
            </Selection>
          </Fragment>
        ))}
      </Box>
    </>
  );
};

export default InvoicesReceiverType;
